import React, { Component, createRef } from 'react'
import Unity, { UnityContext } from "react-unity-webgl";
import CircularProgress from '@material-ui/core/CircularProgress';
import axios from 'axios'
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import ModelControls from '../../../../../Templates/ModelControls';
class ModelPreview extends Component {
    constructor(props) {
        super()
        this.state = {
            webGLLoading: true,
            cameraData: {},
            progression: 0,
            authSent: false,
            loadedAssetBundles: false,
            modelsLoaded: false,
            displayColorPicker: false,
            brightness: true,
            hasError: false
        }
        // Add the missing ref
        this.threeJSContainer = createRef();
        this.modelRef = createRef();

    }

    // Add helper methods for continuous shift, turn, and zoom
    startContinuousShift = (dirX, dirY) => {
        this.shifting = true;
        this.lastShiftTime = null;
        const shiftAnimate = (time) => {
            if (!this.shifting) {
                this.lastShiftTime = null;
                return;
            }
            if (this.lastShiftTime === null) {
                this.lastShiftTime = time;
            }
            const deltaTime = time - this.lastShiftTime;
            const speed = 0.003; // adjust as needed
            const distance = speed * deltaTime;
            // Get the camera's forward vector
            const forward = new THREE.Vector3();
            this.camera.getWorldDirection(forward);
            // Compute the right vector from forward and up
            const right = new THREE.Vector3();
            right.crossVectors(forward, this.camera.up).normalize();
            // Use the camera's up vector
            const up = this.camera.up.clone().normalize();
            // Calculate the shift vector
            const shift = new THREE.Vector3();
            shift.addScaledVector(right, dirX * distance);
            shift.addScaledVector(up, dirY * distance);
            // Apply the shift to the camera's position and the controls target
            this.camera.position.add(shift);
            if (this.controls) {
                this.controls.target.add(shift);
            }
            this.lastShiftTime = time;
            requestAnimationFrame(shiftAnimate);
        };
        requestAnimationFrame(shiftAnimate);
    };

    stopContinuousShift = () => {
        this.shifting = false;
    };

    onShiftUp = () => { this.startContinuousShift(0, 1); }
    onShiftDown = () => { this.startContinuousShift(0, -1); }
    onShiftLeft = () => { this.startContinuousShift(-1, 0); }
    onShiftRight = () => { this.startContinuousShift(1, 0); }
    onShiftStop = () => { this.stopContinuousShift(); }

    rotateLeft = (angle) => {
        // Rotate horizontally about the world up axis (0,1,0)
        const axis = new THREE.Vector3(0, 1, 0);
        // Get offset from target to camera
        const offset = new THREE.Vector3().subVectors(this.camera.position, this.controls.target);
        offset.applyAxisAngle(axis, angle);
        this.camera.position.copy(this.controls.target).add(offset);
        this.camera.lookAt(this.controls.target);
    };

    rotateUp = (angle) => {
        // Rotate vertically about the camera's right vector
        const right = new THREE.Vector3();
        right.crossVectors(this.camera.getWorldDirection(new THREE.Vector3()), this.camera.up).normalize();
        const offset = new THREE.Vector3().subVectors(this.camera.position, this.controls.target);
        offset.applyAxisAngle(right, angle);
        this.camera.position.copy(this.controls.target).add(offset);
        this.camera.lookAt(this.controls.target);
    };

    startContinuousTurn = (direction) => {
        this.turning = true;
        this.lastTurnTime = null;
        const turnAnimate = (time) => {
            if (!this.turning) {
                this.lastTurnTime = null;
                return;
            }
            if (this.lastTurnTime === null) {
                this.lastTurnTime = time;
            }
            const deltaTime = time - this.lastTurnTime;
            const angularSpeed = 0.001; // radians per ms
            const angle = angularSpeed * deltaTime;
            switch(direction) {
                case 'up':
                    this.rotateUp(-angle);
                    break;
                case 'down':
                    this.rotateUp(angle);
                    break;
                case 'left':
                    this.rotateLeft(-angle);
                    break;
                case 'right':
                    this.rotateLeft(angle);
                    break;
                default:
                    break;
            }
            this.lastTurnTime = time;
            requestAnimationFrame(turnAnimate);
        };
        requestAnimationFrame(turnAnimate);
    };

    stopContinuousTurn = () => {
        this.turning = false;
    };

    onTurnUp = () => { this.startContinuousTurn('up'); }
    onTurnDown = () => { this.startContinuousTurn('down'); }
    onTurnLeft = () => { this.startContinuousTurn('left'); }
    onTurnRight = () => { this.startContinuousTurn('right'); }
    onTurnStop = () => { this.stopContinuousTurn(); }

    startContinuousZoom = (direction) => {
        this.zooming = true;
        this.lastZoomTime = null;
        const zoomAnimate = (time) => {
            if (!this.zooming) {
                this.lastZoomTime = null;
                return;
            }
            if (this.lastZoomTime === null) {
                this.lastZoomTime = time;
            }
            const deltaTime = time - this.lastZoomTime;
            const zoomSpeed = 0.005; // adjust speed as needed
            const move = zoomSpeed * deltaTime;
            // Compute unit vector from camera to target
            const vec = new THREE.Vector3();
            vec.subVectors(this.controls.target, this.camera.position).normalize();
            if (direction === 'in') {
                this.camera.position.addScaledVector(vec, move);
            } else if (direction === 'out') {
                this.camera.position.addScaledVector(vec, -move);
            }
            this.camera.lookAt(this.controls.target);
            this.lastZoomTime = time;
            requestAnimationFrame(zoomAnimate);
        };
        requestAnimationFrame(zoomAnimate);
    };

    stopContinuousZoom = () => {
        this.zooming = false;
    };

    onZoomInDown = () => { this.startContinuousZoom('in'); }
    onZoomOutDown = () => { this.startContinuousZoom('out'); }
    onZoomStop = () => { this.stopContinuousZoom(); }

    normalizeObject = (object) => {
        const targetSize = 10; // All models will be scaled to fit within a 10x10x10 cube.

        // 1. Scale the model to a uniform size.
        const initialBox = new THREE.Box3().setFromObject(object);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        const maxDim = Math.max(initialSize.x, initialSize.y, initialSize.z);
        if (maxDim > 0) {
            const scaleFactor = targetSize / maxDim;
            object.scale.set(scaleFactor, scaleFactor, scaleFactor);
        }

        // 2. Center the scaled model at the origin.
        const scaledBox = new THREE.Box3().setFromObject(object);
        const center = scaledBox.getCenter(new THREE.Vector3());
        object.position.sub(center);

        // 3. Place the model on top of the grid (y=0).
        const finalBox = new THREE.Box3().setFromObject(object);
        object.position.y -= finalBox.min.y;
    }

    frameObject = (object, camera, controls) => {
        const padding = 1.5; // Controls how "zoomed out" the camera is. 1.0 is a tight fit.

        // First, normalize the object's scale and position.
        this.normalizeObject(object);
        
        // Get the model's new size and center point for camera framing.
        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        // Calculate the camera distance required to frame the object.
        const fov = camera.fov * (Math.PI / 180);
        const cameraDistance = Math.abs(maxDim / Math.tan(fov / 2)) / 2;

        // Position the camera to achieve a consistent 3/4 viewing angle.
        camera.position.set(
            center.x + cameraDistance,
            center.y + cameraDistance * 0.5,
            center.z + cameraDistance
        ).multiplyScalar(padding);

        // Point the camera at the center of the model.
        controls.target.copy(center);
        controls.update();
    };

    componentDidMount = () => {
        console.log("model props", this.props)
        this.initThreeJSViewer();
    }
    componentWillUnmount = () => {
        // Clean up Three.js resources
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
        }
        
        if (this.renderer) {
            this.renderer.dispose();
        }
    }
    // ----------------------------------------------------------------
    // Three.js viewer setup method
    initThreeJSViewer = () => {
        // Ensure container exists
        if (!this.threeJSContainer.current) return;
        
        // Create scene, camera, and renderer
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0xf0f0f0);
        
        this.camera = new THREE.PerspectiveCamera(
            45,
            this.threeJSContainer.current.clientWidth / this.threeJSContainer.current.clientHeight,
            0.1,
            1000
        );
        this.camera.position.set(0, 5, 10);
        
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(
            this.threeJSContainer.current.clientWidth,
            this.threeJSContainer.current.clientHeight
        );
        this.renderer.setPixelRatio(window.devicePixelRatio);
        
        // Append renderer to the container
        this.threeJSContainer.current.appendChild(this.renderer.domElement);
        
        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
        this.scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(1, 1, 1);
        this.scene.add(directionalLight);
        
        // Add orbit controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        
        // In initThreeJSViewer method:
        const gridHelper = new THREE.GridHelper(100, 100);
        gridHelper.position.y = -0.01; // initial value
        this.scene.add(gridHelper);
        this.gridHelper = gridHelper; // store reference for later
        
        


        // Setup raycaster for click detection
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();

        // Load GLB model
        this.loadGLTFModel();
        // this.loadGLTFModel();
        
        // Handle window resize
        window.addEventListener('resize', this.handleWindowResize);
        
        // Animation loop
        const animate = () => {
            this.frameId = requestAnimationFrame(animate);
            this.controls.update();
            this.renderer.render(this.scene, this.camera);
        };
        animate();
    }
    handleWindowResize = () => {
        if (!this.threeJSContainer.current) return;
        
        // Update camera aspect ratio
        this.camera.aspect = this.threeJSContainer.current.clientWidth / this.threeJSContainer.current.clientHeight;
        this.camera.updateProjectionMatrix();
        
        // Update renderer size
        this.renderer.setSize(
            this.threeJSContainer.current.clientWidth, 
            this.threeJSContainer.current.clientHeight
        );
    }

    // Apply saved textures from texture_config
    applySavedTextures = () => {
        const textureConfig = this.props.model?.texture_config;
        
        // If no texture config exists, skip texture application
        if (!textureConfig) {
            console.log('No texture configuration found for this model');
            return;
        }

        let parsedConfig;
        try {
            // Parse the JSON string
            parsedConfig = typeof textureConfig === 'string' ? JSON.parse(textureConfig) : textureConfig;
        } catch (error) {
            console.error('Error parsing texture configuration:', error);
            return;
        }

        if (!Array.isArray(parsedConfig)) {
            console.error('Texture configuration is not an array:', parsedConfig);
            return;
        }

        console.log('Applying saved textures:', parsedConfig);

        // Apply textures to each configured part group
        parsedConfig.forEach(config => {
            const { partGroup, texturePath, textureName } = config;
            
            if (!partGroup || !texturePath) {
                console.warn('Invalid texture config entry:', config);
                return;
            }

            // Find all meshes with the matching part group
            const meshesToTexture = [];
            this.scene.traverse((node) => {
                if (node.isMesh && node.userData.partGroup === partGroup) {
                    meshesToTexture.push(node);
                }
            });

            if (meshesToTexture.length === 0) {
                console.warn(`No meshes found for part group: ${partGroup}`);
                return;
            }

            // Load and apply the texture
            const textureLoader = new THREE.TextureLoader();
            textureLoader.load(
                texturePath,
                (texture) => {
                    // Configure texture wrapping and repeat options
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    texture.repeat.set(2, 2); // Adjust based on desired scaling

                    // Apply texture to all meshes in the part group
                    meshesToTexture.forEach(mesh => {
                        // Store original material if not already stored
                        if (!mesh.userData.originalMaterial) {
                            mesh.userData.originalMaterial = mesh.material.clone();
                        }

                        // Clone original material and assign the texture
                        const texturedMaterial = mesh.userData.originalMaterial.clone();
                        texturedMaterial.map = texture;
                        texturedMaterial.needsUpdate = true;
                        
                        // Apply the textured material
                        mesh.material = texturedMaterial;
                        
                        // Store texture info in userData for future reference
                        mesh.userData.appliedTexture = {
                            texturePath,
                            textureName,
                            textureIndex: config.textureIndex
                        };
                    });

                    console.log(`Applied texture "${textureName}" to part group "${partGroup}"`);
                },
                undefined,
                (error) => {
                    console.error(`Error loading texture for part group "${partGroup}":`, error);
                }
            );
        });
    }

    loadGLTFModel = () => {
        // Create loading manager to track progress
        const loadingManager = new THREE.LoadingManager();
        loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
            //console.log(`Loading file: ${url} (${itemsLoaded}/${itemsTotal})`);
        };
        
        // Get model ID from props or use a default
        const modelId = this.props.model?.temp_model_id || this.props.model?._id;
        const modelPath = this.props.model.path;
        // console.log(`Loading model with ID ${modelId} and path ${modelPath}`);
        // Create GLTF loader
        const gltfLoader = new GLTFLoader(loadingManager);
        
        // Load the model - Since all approved models are now GLB, construct GLB filename
        const modelName = this.props.model.name || modelPath.replace(/\.[^/.]+$/, ""); // Remove original extension
        const glbFilename = `${modelName}.glb`;
        const modelUrl = `${window.config.getApiBaseUrl()}/api/models/gltf/${modelId}/${glbFilename}`;
        console.log("GLB modelUrl", modelUrl);
        
        // Define base model color and highlight color
        const baseColor = 0x999999; // Light gray base color
        const partColors = [
            0x8C9EFF, // Blue
            0x9CCC65, // Green
            0xFFB74D, // Orange
            0x4FC3F7, // Light blue
            0xF06292, // Pink
            0xBA68C8  // Purple
        ];
        
        gltfLoader.load(
            modelUrl,
            (gltf) => {
                console.log('GLB model loaded successfully', gltf);
                
                // Get the scene from the GLTF result
                const object = gltf.scene;
                
                // Look for mesh groups and preserve them
                let partCounter = 0;
                const objectsMap = new Map(); // To group meshes by objects in GLB file
                
                // First pass: identify groups based on object names
                object.traverse((child) => {
                    if (child.isMesh) {
                        // If child has a parent that's not the root, use parent's name
                        if (child.parent && child.parent !== object) {
                            const objName = child.parent.name || `Part_${partCounter}`;
                            if (!objectsMap.has(objName)) {
                                objectsMap.set(objName, []);
                                partCounter++;
                            }
                            objectsMap.get(objName).push(child);
                        } else {
                            // If no parent or parent is root, use mesh name or generic name
                            const objName = child.name || `Part_${partCounter}`;
                            if (!objectsMap.has(objName)) {
                                objectsMap.set(objName, []);
                                partCounter++;
                            }
                            objectsMap.get(objName).push(child);
                        }
                    }
                });
                
                // Process meshes by group (GLB files often come with materials already)
                objectsMap.forEach((meshes, objName) => {
                    const material = new THREE.MeshPhongMaterial({ color: 0xffffff });
                    // For GLB files, preserve existing materials but add our metadata
                    meshes.forEach(mesh => {
                        // Apply the standardized material
                        mesh.material = material.clone();
                        // Store it as the original material
                        mesh.userData.originalMaterial = material.clone();
                        
                        mesh.name = objName; // Set proper name
                        mesh.userData.partGroup = objName; // Store group info for highlighting
                        mesh.userData.isPickable = true;
                        mesh.userData.isHighlighted = false;
                        mesh.userData.colorIndex = Array.from(objectsMap.keys()).indexOf(objName) % partColors.length;
                    });
                });
                
                // Debug output to see what groups were found
                console.log("Identified object groups:", Array.from(objectsMap.keys()));
                
                // --- Centering and Scaling Logic ---
                this.scene.add(object);
                this.frameObject(object, this.camera, this.controls);

                // Apply saved textures if they exist
                this.applySavedTextures();

                this.setState({ modelsLoaded: true }, () => {
                    if (this.props.onModelsLoaded) {
                      this.props.onModelsLoaded(true);
                    }
                });
            },
            (xhr) => {
                // Loading progress
                if (xhr.lengthComputable) {
                    const percent = (xhr.loaded / xhr.total) * 100;
                    console.log(`GLB Model ${percent.toFixed(2)}% loaded`);
                }
            },
            (error) => {
                // Error loading model
                console.error('Error loading GLB model:', error);
                
                // Set an error state or show fallback content
                this.setState({ hasError: true }, () => {
                    if (this.props.onModelsLoaded) {
                      this.props.onModelsLoaded(false);
                    }
                });
            }
        );
    }
    renderJSModel() {
        return <div 
                  id="webGL-window" 
                  className={!this.props.callFromAuthor ? "flex-centered max-size" : "flex-centered fixedDisplay"}
                  // Here we attach our Three.js container
                  ref={this.threeJSContainer}
                  style={{ position: 'relative', width: '100%', height: '100%' }}
               >
                <ModelControls 
                    onShiftUp={this.onShiftUp}
                    onShiftDown={this.onShiftDown}
                    onShiftLeft={this.onShiftLeft}
                    onShiftRight={this.onShiftRight}
                    onShiftStop={this.onShiftStop} 
                    onTurnUp={this.onTurnUp}
                    onTurnDown={this.onTurnDown}
                    onTurnLeft={this.onTurnLeft}
                    onTurnRight={this.onTurnRight}
                    onTurnStop={this.onTurnStop}   // Pass the turn stop callback
                    onZoomInDown={this.onZoomInDown}
                    onZoomOutDown={this.onZoomOutDown}
                    onZoomStop={this.onZoomStop}
                />
               </div>
    }
    render() {
        return <div id="model-viewer-container" className="flex-centered max-size">
            {this.props.model ? this.renderJSModel() : null}
        </div>
    }
}

export default ModelPreview